/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';
import React, {
  forwardRef,
  ReactElement,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal } from 'components/UiControls/modal/modal';
import { ModalOptions } from 'utils/enum';
import { isEmpty } from 'lodash';
import { ReactComponent as RemoveIcon } from 'assets/Icons/x-cancel-red.svg';
import { Hint } from 'components/shared/Hint';
import { HintDisallowed } from 'components/shared/HintDisallowed';
import { hoverDropShadow } from 'components/shared/twin.styles';
import { WebhookEventType } from 'utils/models/webhook.model';

type IPromiseParams = {
  resolve: (payload: WebhookModalPayload) => void;
  reject: (err: any) => void;
};

type WebhookModalPayload = {
  option: ModalOptions;
  data?: any;
};

export type WebhookModalRef = {
  show: (initialData?: any) => Promise<WebhookModalPayload>;
};

const EVENT_GROUPS = [
  {
    domain: 'Transcription',
    events: [
      WebhookEventType.TRANSCRIPTION_COMPLETED,
      WebhookEventType.AI_PROCESSING_COMPLETED,
    ],
  },
];

export const WebhookModal = forwardRef(
  ({ }, ref: React.Ref<unknown>): ReactElement => {
    useImperativeHandle(ref, () => ({ show }));

    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
    const [headers, setHeaders] = useState<{ key: string; value: string }[]>([]);

    const promiseInfo = useRef<IPromiseParams>({
      resolve: () => { },
      reject: () => { },
    });

    const show = async (initialData?: any): Promise<WebhookModalPayload> => {
      if (initialData) {
        setName(initialData.name || '');
        setUrl(initialData.url || '');
        setSelectedEvents(initialData.events || []);
        const h = Object.entries(initialData.headers || {}).map(([k, v]) => ({
          key: k,
          value: v as string,
        }));
        setHeaders(h);
      } else {
        setName('');
        setUrl('');
        setSelectedEvents([]);
        setHeaders([]);
      }

      return new Promise((resolve, reject) => {
        promiseInfo.current = { resolve, reject };
        setIsOpen(true);
      });
    };

    const hideModal = () => {
      setIsOpen(false);
    };

    const handleCancel = () => {
      hideModal();
      promiseInfo.current?.resolve({ option: ModalOptions.CANCEL });
    };

    const handleSave = () => {
      if (isEmpty(name.trim()) || !isValidUrl(url)) return;

      const headerObj = headers.reduce((acc, curr) => {
        if (!isEmpty(curr.key.trim())) {
          acc[curr.key] = curr.value;
        }
        return acc;
      }, {} as Record<string, string>);

      hideModal();
      promiseInfo.current?.resolve({
        option: ModalOptions.YES,
        data: {
          name: name.trim(),
          url: url.trim(),
          events: selectedEvents,
          headers: headerObj,
        },
      });
    };

    const isValidUrl = (urlString: string) => {
      try {
        const u = new URL(urlString);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch (e) {
        return false;
      }
    };

    const toggleEvent = (event: string) => {
      setSelectedEvents((prev) =>
        prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
      );
    };

    const addHeader = () => {
      if (headers.length < 5) {
        setHeaders([...headers, { key: '', value: '' }]);
      }
    };

    const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
      const newHeaders = [...headers];
      newHeaders[index][field] = val;
      setHeaders(newHeaders);
    };

    const removeHeader = (index: number) => {
      setHeaders(headers.filter((_, i) => i !== index));
    };

    return (
      <div css={[customModalCss]}>
        <Modal
          show={isOpen}
          modalClosed={() => {
            hideModal();
            promiseInfo.current?.resolve({ option: ModalOptions.CLOSE });
          }}
        >
          <div tw="p-2">
            <h2 tw="text-16 font-semibold text-sonnant-purple-3 mb-6">Configure Webhook</h2>

            <div tw="space-y-6">
              {/* Name & URL */}
              <div tw="grid grid-cols-1 gap-4">
                <div>
                  <label tw="block text-14 font-medium text-sonnant-grey-7 mb-1">Webhook Name</label>
                  <input
                    type="text"
                    tw="w-full"
                    placeholder="e.g. My Analytics Service"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div>
                  <label tw="block text-14 font-medium text-sonnant-grey-7 mb-1">Endpoint URL</label>
                  <input
                    type="text"
                    tw="w-full"
                    placeholder="https://api.myapp.com/webhooks"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>
              </div>

              {/* Event Filtering */}
              <div>
                <label tw="block text-14 font-medium text-sonnant-grey-7 mb-3">Event Subscriptions</label>
                <div tw="space-y-6">
                  {EVENT_GROUPS.map((group) => (
                    <div key={group.domain}>
                      <h4 tw="text-13 font-bold text-sonnant-grey-4 uppercase tracking-wider mb-2">
                        {group.domain}
                      </h4>
                      <div tw="grid grid-cols-2 gap-x-4 gap-y-2">
                        {group.events.map((event) => (
                          <label key={event} tw="flex items-start ml-2 cursor-pointer w-auto whitespace-normal">
                            <input
                              type="checkbox"
                              checked={selectedEvents.includes(event)}
                              onChange={() => toggleEvent(event)}
                            />
                            <span className="checkmark" tw="top[4px]! mr-2 flex-shrink-0" />
                            <span tw="text-15 text-sonnant-grey-6 leading-tight pt-1">
                              {event}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Headers */}
              <div>
                <div tw="flex justify-between items-center mb-2">
                  <label tw="block text-14 font-medium text-sonnant-grey-7 m-0!">Custom Headers (Max 5)</label>
                  <button
                    type="button"
                    className="button btn-primary large"
                    tw="bg-sonnant-purple-2 text-white hover:bg-sonnant-purple-1 rounded py-2! text-15"
                    onClick={addHeader}
                    disabled={headers.length >= 5}
                  >
                    + Add Header
                  </button>
                </div>
                <div tw="space-y-2">
                  {headers.map((h, i) => (
                    <div key={i} tw="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Header Name"
                        tw="flex-1 height[3rem]! text-15 text-sonnant-grey-6 m-0!"
                        value={h.key}
                        onChange={(e) => updateHeader(i, 'key', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        tw="flex-1 height[3rem]! text-15 text-sonnant-grey-6 m-0!"
                        value={h.value}
                        onChange={(e) => updateHeader(i, 'value', e.target.value)}
                      />
                      <div tw="flex items-center justify-center">
                        <HintDisallowed
                          disabled={false}
                          hintEnabled='Remove Header'
                        >
                          <div
                            tw="w-[2.4rem] h-[2.4rem] flex items-center justify-center bg-white hover:bg-sonnant-grey-2 cursor-pointer rounded-full"
                            css={[hoverDropShadow(0.1)]}
                            onClick={() => removeHeader(i)}
                          >
                            <RemoveIcon tw="width[1.6rem] height[1.6rem]" />
                          </div>
                        </HintDisallowed>
                      </div>
                    </div>
                  ))}
                  {headers.length === 0 && (
                    <p tw="text-14 text-sonnant-grey-5 italic">No custom headers added.</p>
                  )}
                </div>
              </div>
            </div>

            <div tw="mt-9">
              <div tw="w-full flex justify-end">
                <button
                  type="button"
                  className="button btn-secondary large"
                  tw="flex items-center justify-center mr-6 hover:(border-sonnant-grey-3!) py-2! text-15!"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button btn-primary large"
                  tw="flex items-center justify-center py-2! text-15!"
                  disabled={isEmpty(name.trim()) || !isValidUrl(url)}
                  onClick={handleSave}
                >
                  Save Webhook
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  },
);

const customModalCss = css`
  .Modal {
    ${tw`(width[60rem] left[calc(50% - 30rem)])!`}
  }
`;
